import axios from "axios";
import React from "react";
import { Button, Table } from "react-bootstrap";
import toastr from "toastr";
import { CSVLink } from "react-csv"; 
import { CustomLoading } from "components/CustomLoading";
import "../../styles/Reports.css";
import { EmptyStateTemplate } from "./Empty_state_Template";
import CustomPagination from "components/CustomPagination";
import { DateDropdownWithCalendarPicker } from "./DateDropdownWithCalendarPicker";
import base64 from "../../data/base64.json";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default class CustomCatClaimSummaryReport extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            data: [],
            dataToExport: [],
            selectedOption: this.props.benefitYearOptions[0],
            currentPage: 1,
            itemsPerPage: 12,
            totalClaimAmount: 0,
            showInactiveEmployees: true,
        };

        this.setSelectedOption = this.setSelectedOption.bind(this);
        this.setCurrentPage = this.setCurrentPage.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.getTotals = this.getTotals.bind(this);
        this.toggleInactiveEmployees = this.toggleInactiveEmployees.bind(this);
    }

    componentDidMount() {
        this.setState({loading: true});
        this.fetchData();
    }

    componentDidUpdate(prevProps, prevState) {
        const { selectedOption } = this.state;
        if (prevState.selectedOption !== selectedOption) 
        {
            this.setState({loading: true});
            this.fetchData();
        }
    }

    fetchData = () => {
        const { selectedOption } = this.state;
        var apiString: string;
        if (selectedOption.id === null) {
            apiString = `/api/Reports/customcategoriesclaimsummary?policyId=${this.props.policyId}&fromDate=${selectedOption.startDate.toLocaleString()}&toDate=${selectedOption.endDate.toLocaleString()}`;
        } else {
            apiString = `/api/Reports/customcategoriesclaimsummary?policyId=${this.props.policyId}&benefitYearId=${selectedOption.id}`;
        }
        axios.get(apiString)
            .then(response => {
                if (response.data.success) {
                    const resultToShow = this.state.showInactiveEmployees ? response.data.allResult : response.data.activeOnlyResult;
                    this.getTotals(resultToShow);
                } else {
                    toastr.error(response.data.message, "Error");
                }
                this.setState({loading: false});
            })
            .catch(error => {
                toastr.error(error, "Error");
            });
    }

    setSelectedOption = (option) => {
        this.setState({selectedOption: option});
    }
    
    setCurrentPage = (page) => {
        this.setState({currentPage: page});
    }

    getTotals = (data) => {
        const {
            selectedOption,
        } = this.state;

        let dataToExport = [];
        let totalClaimAmount = 0;

        for (let i = 0; i < data.length; i++) {
            let item = data[i];
            let formattedItem = [
                item.claim_no,
                item.account_type,
                item.total_claim_amount, 
            ];

            totalClaimAmount += parseFloat(item.total_claim_amount);
            dataToExport.push(formattedItem);
        }

        const headerRow = [
            "Claim No", 
            "Account Tye",
            "Claim Enhanced Benefit Total Amount"
        ];

        dataToExport.unshift(headerRow);

        let totalItem = ["Total", ""];

        totalItem.push(totalClaimAmount.toFixed(2));

        dataToExport.push(totalItem);

        const dateRangeRow = [
            "Date Range",
            `${selectedOption.dateRange}`
        ];

        dataToExport.unshift(dateRangeRow);
        
        this.setState({
            data: data,
            dataToExport: dataToExport,
            totalClaimAmount: totalClaimAmount.toFixed(2),
        });
    }

    setSelectedCostCentre = (option) => {
        let findCostCentre = this.state.costCentres.find(cc => cc.name === option);
        this.setState({
            selectedCostCentre: findCostCentre,
        });
    }

    setSelectedDivsion = (option) => {
        let findDivision = this.state.divisions.find(div => div.name === option);
        this.setState({
            selectedDivision: findDivision,
        });
    }

    exportToPdf = () => {
        const { dataToExport } = this.state;
    
        const header = dataToExport[1];
        const bodyRows = dataToExport.slice(2, dataToExport.length - 2);
        const total = dataToExport[dataToExport.length - 2];
      
        const headerData = this.formatRow(header, 'tableHeader');
        const bodyData = this.formatTableBody(bodyRows);
        bodyData.push(this.formatRow(total, 'tableFooter'));
    
        const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'portrait',
            content: [
                {
                    columns: [
                        this.createLogo(),
                        this.createDateRange(dataToExport[0][1]),
                    ],
                },
                this.createTitle(),
                this.createTable(headerData, bodyData)
            ],
            styles: this.getPdfStyles(),
            layout: this.getTableLayout()
        };
    
        pdfMake.createPdf(docDefinition).download("Enhanced_Benefit_Claim_Summary.pdf");
    };
    
    createLogo = () => ({
        image: base64.logo,
        width: 200,
    });

    createTitle = () => ({
        text: 'Enhanced Benefits Claim Summary Report',
        fontSize: 16,
        bold: true,
        alignment: 'center',
        margin: [10, 40, 0, 10]
    });
    
    createDateRange = (dateRange) => ({
        text: `Date Range: ${dateRange}`,
        fontSize: 12,
        bold: true,
        alignment: 'right',
        margin: [0, 0, 0, 8]
    });
    
    formatRow = (rowData, style) => {
        return rowData.map(cell => {
            const isNumeric = !isNaN(parseFloat(cell)) && isFinite(cell);
            const isPercentage = typeof cell === 'string' && cell.includes('%');
    
            return {
                text: String(cell || ''),
                style,
                alignment: (isNumeric || isPercentage) ? 'right' : 'left'
            };
        });
    };
    
    formatTableBody = (rows) => rows.map(row => this.formatRow(row, 'tableBody'));
    
    createTable = (header, body) => ({
        style: 'table',
        table: {
            widths: Array(header.length).fill('auto'),
            body: [header, ...body]
        }
    });
    
    getPdfStyles = () => ({
        table: {
            alignment: 'center',
            margin: [120, 5, 0, 15]
        },
        tableHeader: {
            bold: true,
            fontSize: 12,
            color: 'black'
        },
        tableBody: {
            fontSize: 10,
            color: 'black',
            padding: [2, 2, 2, 2]
        },
        tableFooter: {
            bold: true,
            fontSize: 10,
            color: 'black'
        }
    });
    
    getTableLayout = () => ({
        hLineWidth: (i, node) => 0.5,
        vLineWidth: (i, node) => 0.5,
        paddingLeft: (i, node) => 4,
        paddingRight: (i, node) => 4
    });

    toggleInactiveEmployees = () => {
        this.setState(
            prevState => ({ showInactiveEmployees: !prevState.showInactiveEmployees }),
            () => this.fetchData()
        );
    }
           
    render() {
        const { 
            data,
            selectedOption,
            currentPage,
            itemsPerPage,
            totalClaimAmount,
            loading,
            dataToExport,
            showInactiveEmployees
        } = this.state;

        const {
            benefitYearOptions
        } = this.props;

        const indexOfLastRecord = currentPage * itemsPerPage;
        const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
        const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);
        const nPages = Math.ceil(data.length / itemsPerPage);

        let contents = null;
        if (loading) {
            contents = (<CustomLoading />);
        } else {
            contents = (
                <div className="HSA-Benefit-Utilization-Table report-table-container">
                    <div className="report-toolbar">
                        <div className="toolbar-left-section">
                            <DateDropdownWithCalendarPicker
                                options={[{id:null, dateRange:"Custom", status: "", startDate: "", endDate: ""}, ...benefitYearOptions]}
                                selectedOption={selectedOption}
                                setSelectedOption={this.setSelectedOption}
                            />
                            <div className="benefit-year-status">
                                <span className="benefit-year-status-text">{selectedOption.status}</span>
                            </div>
                            <label className="include-deactivated-employees">
                                <input
                                    className="include-deactivated-employees-checkbox"
                                    type="checkbox"
                                    checked={showInactiveEmployees}
                                    onChange={this.toggleInactiveEmployees}
                                />
                                Include deactivated employees
                            </label>
                        </div>
                        <div className="toolbar-right-section">
                            <div className="export-button-container">
                                <Button 
                                    variant="primary" 
                                    className="export-to-pdf" 
                                    onClick={this.exportToPdf}
                                    disabled={data.length === 0}
                                >
                                    <span className="pdf-text">Export to PDF</span>
                                </Button>
                                <CSVLink 
                                    data={dataToExport} 
                                    filename={"Enhanced_Benefits_Claim_Summary.csv"} 
                                    className="export-button"
                                    onClick={(event) => { if (data.length === 0) event.preventDefault(); }}
                                >
                                    <Button 
                                        variant="primary" 
                                        className="csv-button export-to-csv"
                                        disabled={data.length === 0}
                                    >
                                        <span className="csv-text">Export to CSV</span>
                                    </Button>
                                </CSVLink>
                            </div>
                        </div>
                    </div>

                    {data.length === 0 ? <EmptyStateTemplate />:
                    <>
                        <Table hover className="report-table">
                            <thead>
                                <tr>
                                    <th className="table-text-content">Claim No</th>
                                    <th className="table-text-content">Account Type</th>
                                    <th>Claim Enhanced Benefit Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRecords.map((item, index) => (
                                    <tr key={index} className="table-body-itme">
                                        <td className="table-text-content">{item.claim_no}</td>
                                        <td className="table-text-content">{item.account_type}</td>
                                        <td>{item.total_claim_amount}</td>
                                    </tr>
                                    )
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2} className="table-footer-item total-amount">Total</td>
                                    <td className="table-footer-item">{totalClaimAmount}</td>
                                </tr>
                            </tfoot>
                        </Table>
                        <div className="pagination-container">
                            <div className="pagination-info">
                                <span>Showing {indexOfFirstRecord + 1} - {indexOfFirstRecord+currentRecords.length} entries out of {data.length} entries</span>
                            </div>
                            <CustomPagination 
                                nPages={nPages} 
                                currentPage={currentPage} 
                                setCurrentPage={this.setCurrentPage} 
                            />
                        </div>
                    </>}
                </div>
            );
        }
        
        return (<>{contents}</>);
    }
}